import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import client from "@/lib/apiClient";
import { AiInsights } from "@/components/AiInsights";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { StatCard, SectionHeader, StatusPill, Loader, Lu, STATUS_META, CHART_COLORS, fmtSAR } from "@/components/shared";

export default function Dashboard() {
  const { t, lang } = useLang();
  const { selectedId, selected } = useProperty();
  const [data, setData] = useState(null);
  const [energy, setEnergy] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [rooms, setRooms] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!selectedId) return;
    setData(null);
    Promise.all([
      client.get(`/properties/${selectedId}/overview`),
      client.get(`/properties/${selectedId}/energy`),
      client.get(`/properties/${selectedId}/alerts`),
      client.get(`/properties/${selectedId}/rooms`),
    ]).then(([o, e, a, r]) => {
      setData(o.data); setEnergy(e.data.series || []);
      setAlerts((a.data || []).filter((x) => !x.resolved).slice(0, 5));
      setRooms(r.data || []);
    });
  }, [selectedId]);

  if (!data) return <Loader />;

  const donut = Object.entries(data.by_status).map(([k, v]) => ({ name: k, value: v }));
  const kpis = [
    { icon: "grid-3x3", label: t("totalRooms"), value: data.total_rooms, accent: CHART_COLORS.gold },
    { icon: "users-round", label: t("occupancyRate"), value: `${data.occupancy_pct}%`, accent: CHART_COLORS.emerald },
    { icon: "bell-ring", label: t("openAlerts"), value: data.open_alerts, accent: CHART_COLORS.rose },
    { icon: "zap", label: t("energySaved"), value: `${fmtSAR(data.energy_month_saved_sar)} ${t("sar")}`, sub: `${fmtSAR(data.energy_month_saved_kwh)} kWh`, accent: CHART_COLORS.blue },
  ];

  return (
    <div>
      <SectionHeader icon="layout-dashboard" title={t("dashboard")}
        subtitle={selected ? `${selected[lang === "ar" ? "name_ar" : "name_en"]} · ${selected[lang === "ar" ? "city_ar" : "city_en"]}` : ""} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {kpis.map((k, i) => <StatCard key={k.label} {...k} delay={i * 0.06} testid={`kpi-${i}`} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        {/* Energy trend */}
        <div className="md-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2"><Lu name="zap" className="w-4 h-4 text-primary" />{t("energy")}</h3>
            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/25 font-mono">
              -{data.energy_today_saved_pct}% {lang === "ar" ? "اليوم" : "today"}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={energy}>
              <defs>
                <linearGradient id="gBase" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#64748B" stopOpacity={0.4} /><stop offset="100%" stopColor="#64748B" stopOpacity={0} /></linearGradient>
                <linearGradient id="gAct" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1E5EFF" stopOpacity={0.5} /><stop offset="100%" stopColor="#1E5EFF" stopOpacity={0} /></linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} interval={5} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
              <Area type="monotone" dataKey="baseline_kwh" stroke="#64748B" fill="url(#gBase)" strokeWidth={1.5} name={t("baseline")} />
              <Area type="monotone" dataKey="actual_kwh" stroke="#1E5EFF" fill="url(#gAct)" strokeWidth={2} name={t("actual")} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Status donut */}
        <div className="md-card p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Lu name="pie-chart" className="w-4 h-4 text-primary" />{t("status")}</h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={donut} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={3}>
                {donut.map((d) => <Cell key={d.name} fill={STATUS_META[d.name].color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-2">
            {donut.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-muted-foreground"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_META[d.name].color }} />{t(d.name)}</span>
                <span className="font-mono text-foreground/80">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        {/* Recent alerts */}
        <div className="md-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2"><Lu name="bell-ring" className="w-4 h-4 text-rose-400" />{t("alerts")}</h3>
            <button onClick={() => nav("/alerts")} className="text-xs text-primary hover:underline">{t("all")}</button>
          </div>
          <div className="space-y-2.5">
            {alerts.length === 0 && <p className="text-sm text-muted-foreground/70 py-6 text-center">✓</p>}
            {alerts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg bg-foreground/5 border border-border">
                <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: a.severity === "high" ? "rgba(244,63,94,0.12)" : "rgba(245,158,11,0.12)" }}>
                  <Lu name={a.type === "leak" ? "droplets" : a.type === "battery" ? "battery-low" : a.type === "door" ? "door-open" : "triangle-alert"}
                    className="w-4 h-4" style={{ color: a.severity === "high" ? "#F43F5E" : "#F59E0B" }} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{a[lang === "ar" ? "message_ar" : "message_en"]}</p>
                  <p className="text-xs text-muted-foreground/70">{t("room")} {a.room_number}</p>
                </div>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full shrink-0"
                  style={{ color: a.severity === "high" ? "#F43F5E" : "#F59E0B", background: a.severity === "high" ? "rgba(244,63,94,0.1)" : "rgba(245,158,11,0.1)" }}>
                  {t(a.severity)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick stats */}
        <div className="space-y-4">
          {[
            { icon: "thermometer", label: t("avgTemp"), value: `${data.avg_temp}°`, accent: CHART_COLORS.amber },
            { icon: "wifi", label: t("devicesOnline"), value: `${data.devices_online_pct}%`, accent: CHART_COLORS.emerald },
            { icon: "ticket", label: t("openTickets"), value: data.open_tickets, accent: CHART_COLORS.gold },
          ].map((k, i) => <StatCard key={k.label} {...k} delay={i * 0.05} />)}
        </div>
      </div>
      <div className="mt-5"><AiInsights /></div>
    </div>
  );
}
